import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { mainSiteUrl } from '@/mocks/categories';
import { ArticleJsonLd } from '@/components/base/SeoJsonLd';

export default function ShippingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://kakobuypolska.sbs';
  const pageUrl = `${siteUrl}/kakobuy-dostawa-polska`;

  return (
    <main className="min-h-screen bg-background-50">
      <ArticleJsonLd
        url={pageUrl}
        headline="Kakobuy Dostawa do Polski — Wszystko o Wysyłce, Kosztach i Cłach"
        description="Kompleksowy przewodnik po opcjach dostawy Kakobuy do Polski. Porównujemy metody wysyłki, koszty, czas dostawy oraz wyjaśniamy jak uniknąć problemów z urzędem celnym."
        imageUrl="https://readdy.ai/api/search-image?query=A%20conceptual%20composition%20representing%20international%20shipping%20featuring%20a%20cardboard%20box%20wrapped%20in%20kraft%20paper%20a%20world%20map%20and%20a%20pen%20on%20a%20clean%20light%20oak%20desk%20surface%20soft%20natural%20daylight%20from%20above%20creating%20gentle%20shadows%20warm%20earthy%20tones%20Scandinavian%20editorial%20style%20minimal%20and%20sophisticated%20conveying%20global%20delivery%20with%20ample%20copy%20space&width=1200&height=600&seq=article-shipping-hero&orientation=landscape"
        datePublished="2026-07-20"
        dateModified="2026-07-21"
        authorName="Kakobuy Polska"
      />
      <header className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 text-foreground-950 hover:opacity-80 transition-opacity cursor-pointer whitespace-nowrap">
              <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
                <span className="text-background-50 font-bold text-sm">K</span>
              </div>
              <span className="font-semibold text-base tracking-tight">Kakobuy<span className="text-primary-500">Polska</span></span>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer">
                <i className="ri-arrow-left-line mr-1"></i>Strona główna
              </Link>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="px-5 py-2 text-sm font-semibold bg-primary-500 text-background-50 dark:text-foreground-950 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                Kakobuy Spreadsheet
              </a>
            </div>
          </div>
        </div>
      </header>

      <article className="py-10 md:py-16">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="mb-8 md:mb-10 rounded-2xl overflow-hidden">
            <img src="https://readdy.ai/api/search-image?query=A%20conceptual%20composition%20representing%20international%20shipping%20featuring%20a%20cardboard%20box%20wrapped%20in%20kraft%20paper%20a%20world%20map%20and%20a%20pen%20on%20a%20clean%20light%20oak%20desk%20surface%20soft%20natural%20daylight%20from%20above%20creating%20gentle%20shadows%20warm%20earthy%20tones%20Scandinavian%20editorial%20style%20minimal%20and%20sophisticated%20conveying%20global%20delivery%20with%20ample%20copy%20space&width=1200&height=600&seq=article-shipping-hero&orientation=landscape" alt="Kakobuy dostawa do Polski - wysyłka i cła" title="Kakobuy dostawa Polska - poradnik wysyłki" className="w-full h-48 md:h-72 object-cover object-top" />
          </div>

          <header className="mb-10 md:mb-14">
            <div className="flex items-center gap-3 text-xs md:text-sm text-foreground-500 mb-4">
              <span className="px-2.5 py-1 bg-amber-50 text-amber-600 rounded-full font-medium">Logistyka</span>
              <span>•</span>
              <span>9 min czytania</span>
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4 md:mb-5">
              Kakobuy Dostawa do Polski — Wszystko o Wysyłce, Kosztach i Cłach
            </h1>
            <p className="text-sm md:text-lg text-foreground-600 leading-relaxed">
              Kompleksowy przewodnik po opcjach dostawy Kakobuy do Polski. Porównujemy metody wysyłki, koszty, czas dostawy oraz wyjaśniamy jak uniknąć problemów z urzędem celnym.
            </p>
          </header>

          <div className="prose-custom space-y-6 md:space-y-8">
            <section>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">Dostępne Metody Wysyłki z Kakobuy do Polski</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                <strong>Kakobuy</strong> oferuje kilka różnych opcji dostawy do Polski, różniących się ceną, czasem realizacji i poziomem bezpieczeństwa. Wybór odpowiedniej metody zależy od Twojego budżetu, pilności zamówienia oraz wartości przesyłki. Oto najpopularniejsze opcje:
              </p>
              <ul className="list-disc pl-5 space-y-2 text-sm md:text-base text-foreground-700">
                <li><strong>DHL / FedEx / UPS</strong> — najszybsza opcja (5-10 dni), ale też najdroższa. Idealna dla droższych zamówień, gdzie zależy Ci na szybkiej dostawie i pełnym śledzeniu.</li>
                <li><strong>China Post Registered Air Mail</strong> — ekonomiczna opcja (15-30 dni). Tańsza, ale dłuższa i z ograniczonym śledzeniem.</li>
                <li><strong>EMS (Express Mail Service)</strong> — kompromis między szybkością a ceną (10-20 dni). Dobra opcja dla średniej wielkości paczek.</li>
                <li><strong>Linie kolejowe / transport lądowy</strong> — nowsza opcja łącząca przystępną cenę z akceptowalnym czasem dostawy (14-21 dni).</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">Koszty Wysyłki — Czego Się Spodziewać?</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                Koszt wysyłki zależy od wagi paczki, wymiarów oraz wybranej metody dostawy. Dla pojedynczej pary butów lub jednej bluzy, koszt wysyłki ekspresowej (DHL) może wynosić od 150 do 300 CNY (około 85-170 PLN). Tańsze opcje, jak China Post, to wydatek rzędu 60-120 CNY (35-70 PLN). Warto pamiętać, że Kakobuy umożliwia łączenie kilku produktów w jedną paczkę (tzw. konsolidacja), co znacząco obniża koszt wysyłki w przeliczeniu na jeden produkt. <Link to="/jak-korzystac-z-kakobuy-spreadsheet" className="text-primary-500 hover:text-primary-600 underline">Dowiedz się jak efektywnie korzystać z arkusza</Link>.
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">Cło i VAT — Co Musisz Wiedzieć</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                Od 1 lipca 2021 roku wszystkie przesyłki spoza Unii Europejskiej, w tym z Chin, podlegają opłacie VAT (23% w Polsce) niezależnie od wartości. Dla przesyłek o wartości powyżej 150 EUR naliczane jest również cło, którego stawka zależy od rodzaju towaru. Kakobuy oferuje różne metody deklaracji wartości przesyłki, ale pamiętaj — zaniżanie wartości jest nielegalne i może skutkować zatrzymaniem paczki przez urząd celny.
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">Jak Uniknąć Problemów z Urzędem Celnym?</h2>
              <ul className="list-disc pl-5 space-y-2 text-sm md:text-base text-foreground-700">
                <li><strong>Dokładny opis zawartości</strong> — upewnij się, że sprzedawca lub Kakobuy deklaruje dokładną zawartość paczki.</li>
                <li><strong>Zachowaj dokumentację</strong> — przechowuj faktury i potwierdzenia płatności na wypadek kontroli.</li>
                <li><strong>Wybierz wysyłkę DDP</strong> — niektóre metody wysyłki oferują opcję Delivery Duty Paid, gdzie VAT i cło są opłacone z góry.</li>
                <li><strong>Unikaj podrabianych towarów</strong> — przesyłki zawierające podróbki mogą zostać skonfiskowane.</li>
              </ul>
            </section>

            <div className="bg-primary-50 border border-primary-200/50 rounded-2xl p-6 md:p-8 text-center mt-10">
              <h3 className="text-lg md:text-xl font-bold text-foreground-950 mb-3">Zamów z Pewnością</h3>
              <p className="text-sm md:text-base text-foreground-600 mb-5">Wybierz najlepszą metodę wysyłki i ciesz się zakupami z Chin bez stresu.</p>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center px-8 py-3.5 bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold text-sm md:text-base rounded-xl hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                Otwórz Kakobuy Spreadsheet<i className="ri-arrow-right-up-line ml-2"></i>
              </a>
            </div>

            <div className="border-t border-background-200/60 pt-8 mt-10">
              <h4 className="text-sm font-semibold text-foreground-700 mb-3">Przydatne linki:</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors">← Powrót do strony głównej Kakobuy Polska</Link></li>
                <li><Link to="/poradnik-bezpieczenstwa-kakobuy" className="text-sm text-foreground-500 hover:text-foreground-700 transition-colors">Poradnik bezpieczeństwa Kakobuy</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </article>

      <footer className="bg-foreground-950 text-background-50 py-6">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-500">&copy; {new Date().getFullYear()} KakobuyPolska.sbs — Niezależny przewodnik</p>
          <Link to="/" className="text-xs text-foreground-400 hover:text-background-50 transition-colors cursor-pointer">Strona główna</Link>
        </div>
      </footer>
    </main>
  );
}